import { QueryClient, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useWorkspaceId } from "../bridges/session";
import { api } from "../data/client";
import { isApiError } from "../data/errors";
import { scopedKey } from "../data/keys";
import type { Periodicity, PossibleDuplicateMark, VisitStatusKey } from "../contacts/api";
import { dropWhenUnwatched } from "../contacts/api";
import { visitsKey } from "../visits/api";

/* ---------- types (wire contract: plan 2C "Endpoint summary (as built)") ---------- */

export type AddressMode = "found" | "text_only" | "coordinates";
/** `address_reported` (2D): an approver marked the address a visit report said was wrong; it comes first. */
export type AddressAttention = "address_not_found" | "address_locating" | "address_reported" | null;
export type PersonTitle = "Mr" | "Mrs" | "Ms" | "Dr" | "Rabbi";
export type PersonGender = "male" | "female";

/** `{id, number}` as sent for the house phone and a person's cell. */
export type ContactPhone = { id: number; number: string };
export type ContactWorkPhone = ContactPhone & { extension: string | null };

export type ContactAddress = {
  id: number;
  /** The text to show (formatted text + ", Apt …"). Never send it back as `formatted_address`. */
  line: string;
  mode: AddressMode;
  /** The stored text without the apartment; null when the address is built from its parts. */
  formatted_address: string | null;
  street_name: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  country: string | null;
  apartment_number: string | null;
  note: string | null;
  place_id: string | null;
  latitude: number | null;
  longitude: number | null;
  is_manual: boolean;
  is_text_only: boolean;
  is_do_not_visit: boolean;
  attention: AddressAttention;
};

/**
 * One person. Contact details (`email`, `cell_phone`, `work_phone`, `work_address`) are ABSENT
 * without `can.see_contact_info`.
 */
export type ContactPerson = {
  id: number;
  first_name: string | null;
  last_name: string | null;
  name: string;
  /** One of PersonTitle, or another value a legacy row holds (shown as is, kept unless the user picks a title). */
  title: PersonTitle | (string & {}) | null;
  gender: PersonGender | null;
  is_jewish: boolean | null;
  is_child: boolean;
  is_main: boolean;
  note: string | null;
  email?: string | null;
  cell_phone?: ContactPhone | null;
  work_phone?: ContactWorkPhone | null;
  work_address?: ContactAddress | null;
};

export type ContactCan = {
  update: boolean;
  delete: boolean;
  /** Removing a person, phone or address in Edit needs this (delete rights on the contact). */
  remove_parts: boolean;
  report: boolean;
  see_contact_info: boolean;
  see_history: boolean;
  see_changes: boolean;
  review_changes: boolean;
  /** Today's category list gate: without it there is no categories picker (GET categories answers 403). */
  see_categories: boolean;
  /** 2F: may combine this contact with another (account managers with the full role; never while it waits for approval). Always sent. */
  combine: boolean;
};

/**
 * What waits for approval on this contact (2D `pending`, also the report form's): the values of contact details
 * only with see_contact_info; without read_history only the viewer's own requests.
 */
export type ContactPending = {
  fields: { target: string; field: string; value: unknown; request_id: number }[];
  new_people: { name: string; request_id: number }[];
  people_left: { person_id: number; request_id: number }[];
  left_town: boolean;
  move: boolean;
  sent_by_me_count: number;
  /** UTC `Y-m-d\TH:i:s\Z` of the viewer's latest own change still waiting; null when none (absent from older servers). */
  sent_by_me_at?: string | null;
};

/** The approved "Left town" mark (2D). `at` UTC `Y-m-d\TH:i:s\Z`. */
export type ContactLeftTown = { at: string; by: { id: number; name: string } | null; can_undo: boolean };

export type ContactVisits = {
  status: VisitStatusKey;
  days: number | null;
  last_visit_date: string | null;
  next_planned_date: string | null;
  reports: number;
};

/**
 * One contact (GET households/{id}). Hidden keys are ABSENT, never null: `visits` without
 * `can.see_history`; `house_phone`, `home_address`, `primary` and the persons' detail keys without
 * `can.see_contact_info`; `pending_changes` and `last_change` without `can.see_changes`.
 */
export type ContactDetail = {
  id: number;
  name: string;
  household_name: string | null;
  /** 16 hex chars; send it back as `version` on PATCH. */
  version: string;
  created_at: string | null;
  schedule: { periodicity: Periodicity | null; key: string };
  visits?: ContactVisits;
  house_phone?: ContactPhone | null;
  home_address?: ContactAddress | null;
  primary?: { kind: "home" | "work" | null; person_id: number | null };
  persons: ContactPerson[];
  categories: { id: number; name: string }[];
  pending_changes?: { count: number };
  last_change?: { by: string | null; at: string } | null;
  can: ContactCan;
  /** 2D: `address_reported` when an approver marked a reported address problem (also on that address). */
  attention?: "address_reported" | null;
  /** 2D: absent when not marked. */
  left_town?: ContactLeftTown;
  /** 2D: with read_history, or to a reader who sent one of the waiting requests. */
  pending?: ContactPending;
  /** X3: address id -> unvisited route stops on it; only with see_contact_info and the routes right. */
  unvisited_stops?: Record<string, number>;
  /** 2F: the open "possible duplicate" mark, as on the list row; only for a combiner on a marked contact. */
  possible_duplicate?: PossibleDuplicateMark;
};

export type FormDefaults = {
  default_category_id: number | null;
  periodicity: Periodicity;
  adds_directly: boolean;
  /**
   * `read_contact_info` in this account. Not a gate on the form (anyone may type contact details when adding, owner X1).
   */
  can_see_contact_info: boolean;
  /** Only who holds the full role is answered by the duplicate check (owner 2026-10-21): the form asks only then. */
  checks_duplicates: boolean;
  /** Today's category list gate (as the contact's `can.see_categories`): without it never ask for the category list. */
  can_see_categories: boolean;
  manual_region: { country: string; state: string; name: string } | null;
};

/** A duplicate the asker may not open: no id, name or count is ever sent. */
export type DuplicateHidden = { visible: false };
/** A person of a matching contact, as data (main person first). `cell` (digits as stored) only with read_contact_info. */
export type DuplicatePerson = { first_name: string | null; last_name: string | null; is_child: boolean; cell?: string | null };
/** Visit::REACTIONS as keys. */
export type VisitReaction = "happy" | "warm" | "neutral" | "cold" | "not_interested";
export type DuplicateVisible = {
  visible: true;
  id: number;
  name: string;
  people: DuplicatePerson[];
  categories: string[];
  matched: ("address" | "phone")[];
  /** Only with read_contact_info, only when there is one. */
  address_line?: string;
  house_phone?: string;
  /** Only with read_history, only when visited. */
  /** The latest finished visit; `reaction` null when its report has none. */
  last_visit?: { date: string; reaction: VisitReaction | null };
};
export type DuplicateMatch = DuplicateVisible | DuplicateHidden;

export type DuplicateQuery = {
  names: string[];
  /** The typed household name and people, for the names-only match (one person's first AND last name; the exact household name). */
  household_name?: string;
  people?: { first_name?: string; last_name?: string }[];
  phones: string[];
  address: null | { place_id?: string; street_name?: string; formatted_address?: string; apartment_number?: string };
};

/** One history line. */
export type ChangeSubject = "household" | "person" | "phone" | "address" | "categories" | "schedule" | "primary";
/** 2F-b adds `joined` (a person kept once: the same person on both combined contacts). */
export type ChangeAction = "added" | "changed" | "removed" | "joined";
export type ContactChange = {
  id: number;
  batch: string;
  /** UTC `Y-m-d\TH:i:s\Z`. */
  at: string;
  by: { id: number; name: string; initials: string } | null;
  /** 2D adds `approval`, `report`, `restore`. */
  source: "create" | "edit" | "list" | "bulk" | (string & {});
  subject: ChangeSubject | (string & {});
  subject_id: number | null;
  person_name: string | null;
  field: string | null;
  action: ChangeAction;
  old: unknown;
  new: unknown;
  visit_id: number | null;
  approved_by: { name: string; at: string } | null;
  status: "rejected" | null;
  /** "Put it back" is offered. */
  restorable: boolean;
  /** X2, `address` lines only: each side an AddressInput ready to send back, null when it has none (pick again). */
  picture?: { old: AddressInput | null; new: AddressInput | null };
  /** 2F: moved here from a combined contact; `name` only for readers who may combine (null / absent: not combined). */
  merged_from?: { id: number; name?: string } | null;
  /** 5A: made by ConnectYid staff viewing as `by`; `staff_name` only for staff readers (null: say "ConnectYid staff"). */
  by_staff?: { staff_name: string | null } | null;
};

export type ChangeGroup = "all" | "people" | "categories" | "approvals";

export type ChangesPage = {
  data: ContactChange[];
  meta: { total: number; since: string | null; next_before_id: number | null };
};

/**
 * An address going to the server (AddressIn).
 *
 * - Keeping an existing address: send it back with its keys as received (`id`, `mode`,
 *   `formatted_address`, the parts, `place_id`, the pin, `apartment_number`, `note`,
 *   `is_do_not_visit`) and change only what the user changed. An absent key is unchanged.
 *   Never send `line` as `formatted_address`.
 * - A NEW address from the address pick: the form sends EVERY key below (null where there is none).
 * - A place change on an existing found-mode address requires `formatted_address` (and the new
 *   pick's `place_id` + pin), else 422 on that key.
 * - `found` needs place_id + latitude + longitude; `text_only` needs formatted_address;
 *   `coordinates` needs formatted_address + latitude + longitude.
 */
export type AddressInput = {
  id?: number;
  mode: AddressMode;
  street_name?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  country?: string | null;
  formatted_address?: string | null;
  place_id?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  apartment_number?: string | null;
  note?: string | null;
  is_do_not_visit?: boolean;
};

export type PersonInput = {
  /** Edit: an existing person by id; new ones have no id. A listed person keeps stored names when absent. */
  id?: number;
  /** Names a new person in `primary.person_key`. */
  key?: string;
  first_name?: string | null;
  last_name?: string | null;
  title?: PersonTitle | null;
  gender?: PersonGender | null;
  is_jewish?: boolean | null;
  is_child?: boolean;
  is_main?: boolean;
  note?: string | null;
  /** Contact-detail keys: edit refuses them (403 CONTACT_INFO_NOT_ALLOWED) without see_contact_info. */
  email?: string | null;
  cell_phone?: { number: string } | null;
  work_phone?: { number: string; extension?: string | null } | null;
  work_address?: AddressInput | null;
};

/**
 * The create and edit body (ContactForm).
 * Create: `persons` and `periodicity` required, `version` / `remove_person_ids` prohibited.
 * Edit: `version` required; an absent key is not touched, `null` removes (house_phone, home_address,
 * and a person's phone/address slots); persons not listed are not touched. Without
 * see_contact_info never send house_phone, home_address, primary or a person's email/phones/work_address.
 */
export type ContactPayload = {
  version?: string;
  household_name?: string | null;
  periodicity?: Periodicity;
  category_ids?: number[];
  house_phone?: { number: string } | null;
  home_address?: AddressInput | null;
  primary?: { kind: "home" | "work"; person_id?: number; person_key?: string };
  persons?: PersonInput[];
  remove_person_ids?: number[];
};

/** POST households: a manager gets `{id, pending:false}`; everyone else `{id:null, pending:true}`. */
export type CreateResult = { id: number | null; pending: boolean; name: string };
/** X3: what happens to unvisited route stops on a replaced (`stops`) or removed (`removed_stops`) address. */
export type StopChoices = { stops?: "update" | "leave"; removed_stops?: "remove" | "leave" };
/** PATCH households/{id}: the edit form plus the saver's stop choices (X3). */
export type ContactUpdatePayload = ContactPayload & StopChoices;
/** `contact_details_sent` (X1): contact details from a member without the right went for approval. */
export type UpdateResult = { version: string; changes: number; still_visible: boolean; contact_details_sent: boolean };

/* ---------- errors ---------- */

/** 404 (CONTACT_NOT_FOUND / CHANGE_NOT_FOUND, or a bare 404): show the "not found" page, never retry. */
export const isNotFound = (e: unknown): boolean => isApiError(e) && e.status === 404;

/** The 409 CONTACT_CHANGED refusal: who changed the contact and when (UTC `Y-m-d\TH:i:s\Z`), else null. */
export const conflictOf = (e: unknown): { changedBy?: string; changedAt: string | null } | null => {
  if (!isApiError(e) || e.status !== 409 || e.code !== "CONTACT_CHANGED") return null;
  const body = (e.raw as { response?: { data?: { changed_by?: unknown; changed_at?: unknown } } })?.response?.data;
  return {
    changedBy: typeof body?.changed_by === "string" && body.changed_by ? body.changed_by : undefined,
    changedAt: typeof body?.changed_at === "string" ? body.changed_at : null,
  };
};

/* ---------- queries ---------- */

const BASE = "/api/auth/next/households";

export const useContact = (id: number, opts: { enabled?: boolean } = {}) => {
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: scopedKey(ws, "contact", id),
    queryFn: async () => (await api.get<{ data: ContactDetail }>(`${BASE}/${id}`)).data,
    enabled: ws !== null && (opts.enabled ?? true),
  });
};

export const useFormDefaults = () => {
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: scopedKey(ws, "contact", "form-defaults"),
    queryFn: async () => (await api.get<{ data: FormDefaults }>(`${BASE}/new`)).data,
    enabled: ws !== null,
  });
};

/** Managers only (the server answers 403 CHANGES_MANAGERS_ONLY): pass `see_changes` as `enabled`. */
export const useChanges = (id: number, group: ChangeGroup, opts: { enabled?: boolean } = {}) => {
  const ws = useWorkspaceId();
  return useInfiniteQuery({
    queryKey: scopedKey(ws, "contact", id, "changes", group),
    queryFn: ({ pageParam }) =>
      api.get<ChangesPage>(`${BASE}/${id}/changes`, { group, ...(pageParam ? { before_id: pageParam } : {}) }),
    initialPageParam: null as number | null,
    getNextPageParam: (last) => last.meta.next_before_id ?? undefined,
    enabled: ws !== null && (opts.enabled ?? true),
  });
};

/** One history line (for "Put it back"). */
export const useChange = (id: number, changeId: number, opts: { enabled?: boolean } = {}) => {
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: scopedKey(ws, "contact", id, "change", changeId),
    queryFn: async () => (await api.get<{ data: ContactChange }>(`${BASE}/${id}/changes/${changeId}`)).data,
    enabled: ws !== null && (opts.enabled ?? true),
  });
};

/* ---------- mutations ---------- */

/** The duplicate check is only a hint: it gives up before Save stops waiting for it (3 s), so a hung check fails once and later saves don't wait. */
export const DUPLICATE_TIMEOUT_MS = 2500;

/** Read-only duplicate check: changes nothing, so it invalidates nothing. Resolves to the matches. */
export const useDuplicates = () =>
  useMutation({
    mutationFn: async (q: DuplicateQuery) =>
      (await api.post<{ data: { matches: DuplicateMatch[] } }>(`${BASE}/duplicates`, q, { timeout: DUPLICATE_TIMEOUT_MS })).data.matches,
  });

/**
 * What adding or editing one contact can change in the list: its rows and its numbers (counts, category reach). The
 * category list and the projects stay as they are, so they are not fetched again.
 */
const refreshListAfterSave = (client: QueryClient, ws: number | null) =>
  Promise.all((["list", "counts", "reach"] as const).map((part) => client.invalidateQueries({ queryKey: ["ws", ws, "contacts", part] })));

export const useCreateContact = () => {
  const ws = useWorkspaceId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ContactPayload) => (await api.post<{ data: CreateResult }>(BASE, payload)).data,
    onSuccess: () => refreshListAfterSave(client, ws),
  });
};

export const useUpdateContact = (id: number) => {
  const ws = useWorkspaceId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ContactUpdatePayload) => (await api.patch<{ data: UpdateResult }>(`${BASE}/${id}`, payload)).data,
    // Out of the user's book after the save (`still_visible` false): the contact, its history, lines and visits leave the
    // cache once the edit page has gone (data the user no longer has) and are never fetched again (it would answer
    // 404); the page leaves for the list.
    onSuccess: (result) => {
      // Its visits too: the report form's waiting values and the history cards (notes, address lines).
      if (!result.still_visible) { dropWhenUnwatched(client, ["ws", ws, "contact", id]); dropWhenUnwatched(client, visitsKey(ws, id)); }
      return Promise.all([
        ...(result.still_visible ? [
          client.invalidateQueries({ queryKey: ["ws", ws, "contact", id] }),
          client.invalidateQueries({ queryKey: visitsKey(ws, id) }),
        ] : []),
        refreshListAfterSave(client, ws),
      ]);
    },
  });
};
